import {
  CustomEvaluationsParams,
  ErrorState,
  PrimitiveValueType,
  RowDataObject,
} from "../../../../types";
import { addEmptyEnum } from "./evaluationHelpers/addEmptyEnum";
import { addEmptyEnumForSubkey } from "./evaluationHelpers/addEmptyEnumForSubkey";
import { checkExpenseAllocationTotalDistributionPercentage } from "./evaluationHelpers/checkExpenseAllocationTotalDistributionPercentage";
import {
  enableActiveMonthPostingCutoffDateTime,
  enableActiveMonthPostingCutoffDateTimeForGLCalendar,
} from "./evaluationHelpers/enableActiveMonthPostingCutoffDateTime";
import { enableOrDisableAndClearSaleChainsFieldsByAccountNumber } from "./evaluationHelpers/enableOrDisableAndClearSaleChainsFieldsByAccountNumber";
import { getAccountsReceivableScheduleCodeandScheduleType } from "./evaluationHelpers/getAccountsReceivableScheduleCode";
import { getAccountsReceivableSubType } from "./evaluationHelpers/getAccountsReceivableSubType";
import { getCashInBankReportNumber } from "./evaluationHelpers/getCashInBankReportNumber";
import { handlePOConfigurationGroupAccountsGLStoreOptionsOnStoresChange } from "./evaluationHelpers/handlePOConfigurationGroupAccountsGLStoreOptionsOnStoresChange";
import { handlePOConfigurationGroupShippingInfoDefaultAddress } from "./evaluationHelpers/handlePOConfigurationGroupShippingInfoDefaultAddress";
import { updateDiscountTaxCodesByLaborType } from "./evaluationHelpers/updateDiscountTaxCodesByLaborType";
import { updateCOAGroupCodeDescription } from "./evaluationHelpers/updateCOAGroupCodeDescription";
import {
  updateDefaultFCCompany,
  updateDefaultFCGroup,
  updateDefaultFCStore,
} from "./evaluationHelpers/updateDefaultAccountReceivableFCStoreAndCompany";
import { updateUnitsOfMeasureCode } from "./evaluationHelpers/updatePOUnitsOfMeasureCode";
import { validateWriteOffMaxDebitandMaxCredit } from "./evaluationHelpers/validateWriteOffMaxDebitandMaxCredit";
import { getInventoryBuildingsPriority } from "./evaluationHelpers/getInventoryBuildingsPriority";
import {
  checkPriceBreakEscalatorRangeOverlap,
  checkPriceBreakEscalatorValue,
} from "./evaluationHelpers/checkPriceBreakOverLapppingRanges";
import { handleEnableOrDisableForIsPriorDaysUseForOT } from "./evaluationHelpers/handleEnableOrDisableForIsPriorDaysUseForOT";

// This function is used to handle custom evaluations on certain field value changes
export function customEvaluations({
  evalMethod,
  propertyKey,
  propertySubKey,
  rowIndex,
  inputValue,
  searchResponse,
  selectedTabSectionKey,
  rowDataReducerState,
  dispatch,
}: CustomEvaluationsParams): void | ErrorState {
  switch (evalMethod) {
    case "getAccountsReceivableScheduleCodeandScheduleType":
      return getAccountsReceivableScheduleCodeandScheduleType(
        propertyKey,
        rowDataReducerState,
        dispatch,
        inputValue as string,
      );

    case "getAccountsReceivableSubType":
      return getAccountsReceivableSubType(
        rowDataReducerState,
        dispatch,
        inputValue as string,
      );

    case "getCashInBankReportNumber":
      return getCashInBankReportNumber(
        propertyKey,
        rowDataReducerState,
        dispatch,
      );

    case "enableOrDisableAndClearSaleChainsFieldsByAccountNumber":
      return enableOrDisableAndClearSaleChainsFieldsByAccountNumber(
        propertyKey,
        dispatch,
        inputValue as PrimitiveValueType,
        searchResponse,
      );

    case "checkExpenseAllocationTotalDistributionPercentage":
      return checkExpenseAllocationTotalDistributionPercentage(
        propertyKey,
        propertySubKey,
        rowIndex,
        inputValue as PrimitiveValueType | RowDataObject[],
        rowDataReducerState,
        dispatch,
      );

    case "enableActiveMonthPostingCutoffDateTime":
      return enableActiveMonthPostingCutoffDateTime(
        propertyKey,
        propertySubKey,
        inputValue as PrimitiveValueType,
        rowDataReducerState,
        dispatch,
      );

    case "updateCOAGroupCodeDescription":
      return updateCOAGroupCodeDescription(
        propertyKey,
        inputValue as PrimitiveValueType,
        searchResponse,
        dispatch,
      );

    case "updateDiscountTaxCodesByLaborType":
      return updateDiscountTaxCodesByLaborType(
        propertyKey,
        inputValue as PrimitiveValueType,
        searchResponse,
        selectedTabSectionKey,
        rowDataReducerState,
        dispatch,
      );

    case "validateWriteOffMaxDebitandMaxCredit":
      return validateWriteOffMaxDebitandMaxCredit(
        propertyKey,
        inputValue as PrimitiveValueType,
        rowDataReducerState,
        dispatch,
      );

    case "handlePOConfigurationGroupShippingInfoDefaultAddress":
      return handlePOConfigurationGroupShippingInfoDefaultAddress(
        propertyKey,
        propertySubKey,
        rowIndex,
        inputValue as boolean,
        selectedTabSectionKey as string,
        rowDataReducerState,
        dispatch,
      );

    case "handlePOConfigurationGroupAccountsGLStoreOptionsOnStoresChange":
      return handlePOConfigurationGroupAccountsGLStoreOptionsOnStoresChange(
        inputValue as RowDataObject[],
        selectedTabSectionKey as string,
        rowDataReducerState,
        dispatch,
      );

    case "updateUOMCode":
      return updateUnitsOfMeasureCode(
        propertyKey,
        inputValue as PrimitiveValueType,
        dispatch,
      );

    case "addEmptyRoleLevelEnumForPORoleLevelInfo":
    case "addRemittanceFormMappingEmptyEnumForFormId":
    case "addRemittanceFormMappingEmptyEnumForProvisionId":
      return addEmptyEnumForSubkey(
        propertyKey,
        propertySubKey as string,
        dispatch,
      );

    case "addEmptyAccountingAccountEnum":
    case "addDefaultLaborTypesEmptyEnumForDefaultLaborType":
    case "addDefaultLaborTypesEmptyEnumForCustomerPayType":
    case "addDefaultLaborTypesEmptyEnumForWarrantyPayType":
    case "addDefaultLaborTypesEmptyEnumForInternalPayType":
      return addEmptyEnum(propertyKey, dispatch);

    case "updateDefaultFCStore":
      return updateDefaultFCStore(
        rowDataReducerState,
        dispatch,
        inputValue as string,
      );

    case "updateDefaultFCCompany":
      return updateDefaultFCCompany(
        rowDataReducerState,
        dispatch,
        inputValue as string,
      );
    case "updateDefaultFCGroup":
      return updateDefaultFCGroup(
        rowDataReducerState,
        dispatch,
        inputValue as string,
      );

    case "getInventoryBuildingsPriority":
      return getInventoryBuildingsPriority({
        propertyKey,
        rowDataReducerState,
        dispatch,
      });

    case "checkPriceBreakEscalatorRangeOverlap":
      return checkPriceBreakEscalatorRangeOverlap(
        propertyKey,
        rowIndex,
        rowDataReducerState,
        dispatch,
      );

    case "checkPriceBreakEscalatorValue":
      return checkPriceBreakEscalatorValue(
        propertyKey,
        propertySubKey,
        rowIndex,
        inputValue as PrimitiveValueType,
        rowDataReducerState,
        dispatch,
      );

    case "enableGLCalendarActiveMonthPostingCutoffDateTime":
      return enableActiveMonthPostingCutoffDateTimeForGLCalendar(
        propertyKey,
        inputValue as PrimitiveValueType,
        rowDataReducerState,
        dispatch,
      );

    case "handleEnableOrDisableForIsPriorDaysUseForOT":
      return handleEnableOrDisableForIsPriorDaysUseForOT(
        propertyKey,
        inputValue as PrimitiveValueType,
        rowDataReducerState,
        dispatch,
      );

    default:
      return;
  }
}
